import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Collapse } from '@mui/material';
import SmallToggleRow, { SmallToggleRowStandalone } from '../components/SmallToggleRow';
import MessagePreview from '../components/MessagePreview';
import AdvancedSection from '../components/AdvancedSection';

const APPT_VARS = ['{{first_name}}', '{{practice_name}}'];
const HOURS_VARS = ['{{practice_name}}', '{{opening_hours}}'];
const LOCATION_VARS = ['{{practice_name}}', '{{practice_address}}', '{{maps_link}}'];
const URGENT_VARS = ['{{first_name}}', '{{practice_phone}}'];

const ITEMS = [
    { key: 'appt_ack_enabled', label: '\u{1F4C5}  Booking requests', desc: 'Auto-reply to appointment booking or rescheduling messages.' },
    { key: 'auto_reply_hours', label: '\u{1F550}  Opening hours', desc: 'WhatsApp AI replies with your current opening hours.' },
    { key: 'auto_reply_location', label: '\u{1F4CD}  Address & directions', desc: 'WhatsApp AI sends your address with a Google Maps link.' },
    { key: 'urgent_detect', label: '\u{1F6A8}  Dental emergencies', desc: 'Auto-reply with emergency info when pain or urgent keywords are detected.' },
];

export default function AutoRepliesStep({ formData, onChange, practiceName, practiceLogo, practiceAddress, practiceOpeningHours, practicePhone }) {
    const [expandedKey, setExpandedKey] = React.useState(null);

    const apptSample = {
        first_name: 'Sarah',
        practice_name: practiceName || 'Your Practice',
    };
    const hoursSample = {
        practice_name: practiceName || 'Your Practice',
        opening_hours: practiceOpeningHours || 'Mon-Fri 9am-5pm\nSat 9am-1pm',
    };
    const locationSample = {
        practice_name: practiceName || 'Your Practice',
        practice_address: practiceAddress || '123 High Street, London',
        maps_link: 'maps.google.com/...',
    };
    const urgentSample = {
        first_name: 'Sarah',
        practice_phone: practicePhone || '0123 456 7890',
    };

    const previewConfig = {
        appt_ack_enabled: { message: formData.appt_message, msgKey: 'appt_message', variables: APPT_VARS, sampleValues: apptSample },
        auto_reply_hours: { message: formData.hours_message, msgKey: 'hours_message', variables: HOURS_VARS, sampleValues: hoursSample },
        auto_reply_location: { message: formData.location_message, msgKey: 'location_message', variables: LOCATION_VARS, sampleValues: locationSample },
        urgent_detect: { message: formData.urgent_message, msgKey: 'urgent_message', variables: URGENT_VARS, sampleValues: urgentSample },
    };

    const handleToggle = (key, val) => {
        onChange(key, val);
        if (val) {
            setExpandedKey(key);
        } else if (expandedKey === key) {
            setExpandedKey(null);
        }
    };

    const handleRowClick = (key) => {
        if (formData[key]) {
            setExpandedKey(expandedKey === key ? null : key);
        }
    };

    return (
        <Box>
            <Typography sx={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#00A76F', mb: '10px',
            }}>
                Auto-replies
            </Typography>
            <Typography sx={{
                fontSize: 22, fontWeight: 800, color: '#0D0D0D',
                lineHeight: 1.3, letterSpacing: '-0.4px', mb: 1,
            }}>
                Set up automatic replies
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: '#7A7A7A', lineHeight: 1.6, mb: '28px' }}>
                Choose which common questions WhatsApp AI should handle automatically — using information from your practice settings.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', mb: '20px' }}>
                {ITEMS.map(({ key, label, desc }) => {
                    const isOn = formData[key] ?? false;
                    const isExpanded = isOn && expandedKey === key;
                    const cfg = previewConfig[key];

                    return (
                        <Box key={key}>
                            <SmallToggleRowStandalone
                                checked={isOn}
                                onChange={(val) => handleToggle(key, val)}
                                label={label}
                                desc={desc}
                                onLabelClick={() => handleRowClick(key)}
                            />
                            <Collapse in={isExpanded} timeout={250} unmountOnExit>
                                <Box sx={{ mt: '6px' }}>
                                    <MessagePreview
                                        message={cfg.message}
                                        onChange={(val) => onChange(cfg.msgKey, val)}
                                        variables={cfg.variables}
                                        sampleValues={cfg.sampleValues}
                                        practiceName={practiceName}
                                        practiceLogo={practiceLogo}
                                    />
                                </Box>
                            </Collapse>
                        </Box>
                    );
                })}

                {/* Pricing — coming soon */}
                <Box sx={{ position: 'relative' }}>
                    <SmallToggleRowStandalone
                        checked={false}
                        onChange={() => {}}
                        label="💷  Pricing questions"
                        desc="Allow WhatsApp AI to respond to pricing enquiries."
                        disabled
                    />
                    <Box sx={{
                        position: 'absolute',
                        top: 10,
                        right: 50,
                        px: 1,
                        py: '2px',
                        borderRadius: '6px',
                        backgroundColor: '#E3F2FD',
                        color: '#1976D2',
                        fontSize: '10px',
                        fontWeight: 700,
                        letterSpacing: '0.03em',
                    }}>
                        Coming soon
                    </Box>
                </Box>
            </Box>

            <AdvancedSection>
                <SmallToggleRow
                    checked={formData.send_booking_link ?? false}
                    onChange={(val) => onChange('send_booking_link', val)}
                    label="Include your online booking link"
                    desc="WhatsApp AI appends your online booking link (from Settings) when it detects a new appointment request."
                />
            </AdvancedSection>
        </Box>
    );
}

AutoRepliesStep.propTypes = {
    formData: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
};
